export type PlatformCapability = "microphone" | "push-notifications" | "file-storage" | "camera";
export type PlatformType = "web" | "android" | "ios";

export interface PushPayload {
  title: string;
  body: string;
  data?: Record<string, string>;
}

/**
 * Age band reported by Apple's Declared Age Range API (iOS 18+) or Google's
 * Play Age Signals. `null` means "the platform did not tell us" — which is the
 * answer on web and on any OS without the API, and must never be confused with
 * "this user is an adult".
 *
 * ipad-native-shell R7.4. As of 2026 this stopped being purely advisory: Utah
 * (2026-05-06) and Louisiana (2026-07-01) share age categories with apps on
 * request, so the call site is a real integration point, not decoration.
 */
export type DeclaredAgeRange = { min: number | null; max: number | null } | null;

export type NetworkStatus = { online: boolean };

export interface ShareContent {
  title?: string;
  text?: string;
  url: string;
}

/**
 * `unavailable` means "no system share sheet here" — the caller falls back to
 * copy-link. `cancelled` means the sheet opened and the person closed it, which
 * is not an error and must not fall back.
 */
export type ShareOutcome = "shared" | "cancelled" | "unavailable";

/** A picture a project handed the host to save (game-on-freeform U7, `wbm:save_image`). */
export interface ShareImage {
  /** The image bytes, base64, without the `data:` prefix. */
  base64: string;
  mime: "image/png" | "image/jpeg";
  /** A file name with its extension. */
  name: string;
}

/**
 * `downloaded` means the web fell back to a file download (no share sheet for files
 * here, or a keyboard device, where a download is what a person expects). `saved` means
 * the native shell put it straight into Photos (U18).
 */
// "saved": straight to Photos in the native app, no share sheet (the iPad lane's WBMPhotos).
export type ShareImageOutcome = ShareOutcome | "downloaded" | "saved";

export interface PlatformAdapter {
  /**
   * The current microphone permission, WITHOUT ever showing a system prompt
   * (R9.4: the child sees our "Your turn to talk" context screen first). Only
   * `getMicStream()` may prompt, and only after that screen's button.
   */
  queryMicPermission(): Promise<PermissionState>;
  getMicStream(): Promise<MediaStream>;
  releaseMicStream(): void;
  saveLocal(key: string, data: Uint8Array): Promise<void>;
  loadLocal(key: string): Promise<Uint8Array | null>;
  deleteLocal(key: string): Promise<void>;
  registerPushToken(): Promise<string>;
  onPushReceived(handler: (payload: PushPayload) => void): void;
  getPlatform(): PlatformType;
  getScreenSize(): { width: number; height: number };
  isCapabilityAvailable(cap: PlatformCapability): boolean;

  /**
   * Current connectivity (R7.5). The "kid's iPad loses wifi mid-plan" case in
   * `.kiro/reviews/slice4-7-spec-gaps.md:54`.
   */
  isOnline(): boolean;
  /** Subscribe to connectivity changes. Returns an unsubscribe function. */
  onNetworkChange(handler: (status: NetworkStatus) => void): () => void;

  /** Platform-declared age band, or `null` when the OS does not provide one. */
  getDeclaredAgeRange(): Promise<DeclaredAgeRange>;

  /**
   * Configure platform audio for a voice conversation, and release it after
   * (R3.3). A no-op everywhere except iOS, where WKWebView's defaults route
   * agent speech to the earpiece and silence it under the hardware mute
   * switch. Never throws — session start must not depend on this succeeding.
   */
  activateVoiceAudio(): Promise<void>;
  deactivateVoiceAudio(): Promise<void>;

  /**
   * Project sound plays through the ringer switch (sound-and-music §2).
   * Request on the first sound that plays, never on mount; release when the
   * project's sound stops for good. A live voice session wins and playback
   * returns when it ends. Idempotent; never throws. iOS: `.playback` mixing
   * with other audio; web: `navigator.audioSession.type`, where it exists.
   */
  requestSoundPlayback(): Promise<void>;
  releaseSoundPlayback(): Promise<void>;

  /**
   * Open the system share sheet (R16.1). Never throws. Callers keep the
   * Parental_Gate in front of it; the adapter does not gate.
   */
  share(content: ShareContent): Promise<ShareOutcome>;

  /**
   * Save or share a picture (game-on-freeform U7). Never throws. `prefer: "share"` asks
   * for the share sheet where one takes files (a touch device), else the web downloads
   * the file. Must run inside the tap that asked for it: the web share sheet needs that
   * gesture. Callers keep the Parental_Gate in front of it for a child.
   */
  shareImage(image: ShareImage, prefer: "share" | "download"): Promise<ShareImageOutcome>;

  /**
   * Whether `shareImage` saves straight to Photos (the native shell's WBMPhotos, U18)
   * rather than through a share sheet. True means the picture never leaves the device,
   * so no grown-up check is needed in front of it. False on web and on older shells.
   */
  canSaveToPhotos(): boolean;

  /**
   * Subscribe to links that open the app (Universal Links, R16.2). The handler
   * gets the in-app path (`/share/abc?x=1#y`) for our own hosts only; any other
   * host is dropped here. Returns an unsubscribe function. A no-op on web, where
   * the browser already navigated.
   */
  onAppUrlOpen(handler: (path: string) => void): () => void;

  /**
   * The app went to the background (iOS didEnterBackground, Capacitor App
   * "pause"). Returns an unsubscribe function. Undefined off-native. Not
   * fired for inactive (the app switcher, Control Center), and nothing at all
   * is delivered when the app is killed.
   */
  onAppBackground?: (handler: () => void) => () => void;

  /**
   * The app came back to the foreground (Capacitor App "resume"). Returns an
   * unsubscribe function. Undefined off-native.
   */
  onAppResume?: (handler: () => void) => () => void;

  /**
   * One light "that worked" tap (R16.3) — save, mission step. Nothing else
   * should vibrate. A no-op off-native. Never throws.
   */
  hapticConfirm(): Promise<void>;

  /**
   * Hold the screen on for an active voice session, and let it sleep again
   * after (R7.6). A no-op off-native. Never throws.
   */
  setKeepAwake(on: boolean): Promise<void>;

  /**
   * The installed app's marketing version and build (R15.1), which `api.ts`
   * sends as `X-WBM-Client` so a stale shell is visible and can be told to
   * update. `null` on web (the page is always current) and on any failure.
   * Never throws.
   */
  getAppVersion(): Promise<{ version: string; build: string } | null>;

  /**
   * Fade out the native launch splash (task 14.8). Undefined where there is no
   * splash (web), which `boot/splash.ts` treats as "nothing to hide".
   */
  hideSplash?: (options: { fadeOutDuration: number }) => Promise<void>;
}

/**
 * Base64 helpers shared by the adapters.
 *
 * `btoa(String.fromCharCode(...bytes))` — the previous implementation — spreads
 * every byte as a separate function argument. Past roughly 100k elements that
 * exceeds the engine's argument limit and throws `RangeError: Maximum call
 * stack size exceeded`, so saving any real project blob crashed. Chunking keeps
 * each spread small regardless of payload size.
 */
const BASE64_CHUNK = 0x8000;

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK));
  }
  return btoa(binary);
}

export function base64ToBytes(encoded: string): Uint8Array {
  const binary = atob(encoded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

export const UNAVAILABLE_MESSAGES: Record<PlatformCapability, string> = {
  microphone: "Oops! I can't hear you right now. Ask a grown-up to let me use the microphone!",
  "push-notifications": "I can't send you reminders right now. Ask a grown-up to turn on notifications!",
  "file-storage": "I can't save things right now. Try again in a little bit!",
  camera: "I can't use the camera right now. Ask a grown-up for help!",
};
